/*
 * jbc-classes.js — Julie Berkowitz Ceramics, Online Store 2.0 theme (workstream N).
 *
 * A rewrite of the Phase 1 file against Flex 7.0.0, not a copy. No jQuery, no dependency on
 * any Flex global beyond the two verified below.
 *
 * HOW FLEX 7 SELECTS A VARIANT (and where we join in)
 * ---------------------------------------------------
 * Flex 7 replaced the vintage option dropdowns with a `<kit-variant-picker>` custom element
 * (assets/kit.product-variant-picker.js). On a real `change` inside it, it re-fetches the
 * product section from Shopify (`?option_values=…&section_id=…`), swaps its own innerHTML —
 * including the hidden `<input name="id">` that actually submits — and emits
 * `kit:variant-picker:after-change`. `assets/kit-variant-picker-bridge.js` turns that into a
 * bubbling `variant-change` CustomEvent on `[data-product-form]`, carrying
 * `{ product, variant, state }`. `assets/z__jsProduct.js` listens for that event on the form
 * and calls `_switchVariant`, which calls `window.selectCallback` (assets/utilities.js) for
 * the button, notify form, quantity box and inventory line, and then the price UI.
 *
 * So `variant-change` is the seam, and it is the one we use — in both directions:
 *
 *   - we LISTEN for it on the product container (a bubbling listener added after Flex's own
 *     listener on the form, so Flex has always finished first), and
 *   - we DISPATCH it ourselves when a customer picks one of our section radios, after setting
 *     the hidden `input[name="id"]` and mirroring the choice into Flex's own option controls.
 *
 * Dispatching rather than driving kit's `change` is deliberate. Kit's change is a network
 * round trip per click, and its failure path is `window.location.assign(productUrl)` — a full
 * page load because someone picked a radio. Dispatching is synchronous, needs no network, and
 * still leaves every piece of Flex's own UI (button state, price, notify form, quantity) to
 * Flex. The cost is that `detail.variant` must be a complete variant object, which is why
 * `snippets/jbc-class-data.liquid` emits Shopify's own variant JSON per section.
 *
 * `window.selectCallback` still exists in Flex 7 (assets/utilities.js:917). We do not wrap it:
 * it is called from inside `_switchVariant` and wrapping it would put our code between Flex
 * and its own price UI. Nothing here edits a Flex asset.
 *
 * Degradation: every entry point is inside a try/catch, and with JavaScript off the picker is
 * hidden by CSS and Flex's own option selectors take over (see assets/jbc.css).
 *
 * The pure logic below — the state ladder, the date wording, the overflow gate — is exported
 * for `scripts/jbc-classes.test.mjs`. `sectionState()` is the same seven-branch ladder as
 * `snippets/jbc-section-state.liquid`; if one changes the other must.
 */
(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module && module.exports) {
    module.exports = api;
  } else {
    root.jbcClasses = api;
  }

  if (typeof document !== 'undefined') {
    api.start();
  }
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  var STATES = ['open', 'soon', 'full', 'started', 'plain', 'dormant', 'retired'];

  /* ---------------------------------------------------------------- pure */

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* Today as 'YYYY-MM-DD' in the browser's own time zone. Only ever the fallback — see
     storeClock(). */
  function todayISO(now) {
    var d = now || new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  /*
   * The clock a failed parse hands on: one minute to midnight.
   *
   * Every fallback in this file **fails closed** (QA Q5). If the browser cannot work out the
   * store's time, the one thing it must not do is decide that a class time which the server
   * rendered as `started` is on sale again. 23:59 is at or after every start time there can
   * be, so the browser can only ever agree with a server-rendered `started` or reach it
   * sooner — never leave it.
   *
   * The cost is the other direction: on a start day, with an unreadable offset, the browser
   * closes a class time that Liquid still showed as open. That is a seat not sold, which is
   * recoverable; the opposite is a seat sold into a class that has already met, which is not.
   */
  var CLOSED_CLOCK = '23:59';

  /*
   * THE clock, for everything this file decides.
   *
   * `snippets/jbc-class-data.liquid` stamps the page with the store's own date
   * (`'now' | date: '%Y-%m-%d'`, Chapel Hill) as `data-jbc-today`, and that is the date used
   * here — so the chips Liquid rendered and the notify wording and overflow line this file
   * decides are answers to the same question. Reading `new Date()` instead meant a visitor
   * in another time zone could be shown the "Opens October 21" chip from Liquid beside the
   * *full* notify wording and "This section is full — … has seats" from JavaScript.
   *
   * The browser's date is the fallback for a missing or malformed attribute only.
   */
  function storeToday(stamped, now) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(stamped)) ? String(stamped) : todayISO(now);
  }

  /*
   * ONE CLOCK, AMENDED (plan T3, 2026-09-17). A date stamp was enough while every state was
   * day-granular. The `started` state is not: a seat may be bought up to the moment the first
   * class starts, and Shopify serves a rendered page from cache for minutes afterwards, so
   * the date Liquid stamped can be right while the *time* it decided with is stale.
   *
   * So Liquid stamps both halves of its own clock — the date as `data-jbc-today` and the
   * store's UTC offset (`'now' | date: '%z'`, e.g. `-0400`) as `data-jbc-offset` — and the
   * browser rebuilds the store's date and time from its own `Date.now()` plus that offset.
   * The visitor's time *zone* never enters into it; only the instant does, and that is the
   * same instant everywhere. `nowMs` is injectable so the tests can hold the clock still.
   *
   * A missing or malformed offset falls back to the stamped date and to `CLOSED_CLOCK`, not to
   * the browser's own wall clock (QA Q5). The date can safely be approximate — it is the
   * server's own answer — but the *time* cannot, because the only way to be wrong about it
   * safely is to be late.
   */
  function parseOffset(text) {
    var m = /^([+-])(\d{2})(\d{2})$/.exec(String(text == null ? '' : text).trim());
    if (!m) return null;
    var hours = Number(m[2]);
    var minutes = Number(m[3]);
    if (hours > 23 || minutes > 59) return null;
    return (m[1] === '-' ? -1 : 1) * (hours * 60 + minutes);
  }

  function storeClock(stamped, offsetText, nowMs, browserNow) {
    var offset = parseOffset(offsetText);
    if (offset === null) {
      return { today: storeToday(stamped, browserNow), now: CLOSED_CLOCK };
    }
    /* Shifted into UTC and then read in UTC, so the browser's own zone cannot leak in. */
    var shifted = new Date(
      (typeof nowMs === 'number' ? nowMs : Date.now()) + offset * 60000
    );
    return {
      today: shifted.getUTCFullYear()
        + '-' + pad(shifted.getUTCMonth() + 1)
        + '-' + pad(shifted.getUTCDate()),
      now: pad(shifted.getUTCHours()) + ':' + pad(shifted.getUTCMinutes())
    };
  }

  /*
   * The start of a `jbc.time_range` as 'HH:MM', zero-padded so it compares as a string:
   * '18:30-20:30' -> '18:30', '9:30-11:30' -> '09:30'.
   *
   * **Exactly `HH:MM-HH:MM`**: two halves, digits only, an hour of 00-23, an hour allowed to
   * drop its leading zero. `snippets/jbc-clock.liquid` is the same three rules spelled out in
   * Liquid, which has no regular expressions — a range one of them can read and the other
   * cannot is a row whose chip and whose button disagree about whether a seat is for sale.
   *
   * Anything refused is '': the caller treats that as "no time recorded" and closes the class
   * time from midnight on its start day, never at 23:59.
   */
  function startClockOf(range) {
    var m = /^\s*(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*$/
      .exec(String(range == null ? '' : range));
    if (!m) return '';
    var padded = pad(Number(m[1])) + ':' + m[2];
    if (padded > '23:59') return '';
    /* The far half has to be a readable time too, exactly as the Liquid requires it. */
    var end = pad(Number(m[3])) + ':' + m[4];
    return end > '23:59' ? '' : padded;
  }

  /*
   * Has the first class met? `starts_on` before today, or `starts_on` today and the start of
   * `jbc.time_range` at or before the store's time now.
   *
   * A start day with no readable `time_range` counts as started from midnight. The rule
   * protects Julie from a sale she cannot honour, so a missing time has to close early rather
   * than late — the same direction the whole state takes.
   */
  function hasStarted(entry, today, clock) {
    if (!entry || !entry.starts_on) return false;
    if (entry.starts_on < today) return true;
    if (entry.starts_on !== today) return false;
    var start = startClockOf(entry.time_range);
    if (!start) return true;
    /* A clock we were not given, or cannot read, is 23:59: see CLOSED_CLOCK. */
    return start <= (/^\d{2}:\d{2}$/.test(String(clock)) ? String(clock) : CLOSED_CLOCK);
  }

  /*
   * THE state rule. Identical, branch for branch, to snippets/jbc-section-state.liquid.
   *
   *   dormant — no jbc.term and not available, or jbc.not_running and not available;
   *             not rendered at all
   *   plain   — no jbc.term and available; rendered, with its real action
   *   retired — ends_on is before today; not rendered at all
   *   started — the first class has met; shown, muted, and nothing to click
   *   soon    — opens_on is after today                     (held is never full)
   *   open    — available
   *   full    — not available, opens_on today, past or blank
   *
   * The invariant is "never hide a variant a customer could buy", not "never hide a variant"
   * (plan T3, amended 2026-09-15). Every class product carries placeholder variants for
   * seasons the rollover has not planned — no term, no inventory — and offering *Reserve a
   * seat* on one is a lie. Those are `dormant`. A termless variant that IS available is
   * `plain` and is still rendered.
   *
   * A section Julie switched off in the Studio carries `jbc.not_running` (docs/adr/0021).
   * With no seats it is `dormant` too: without the mark it read as *Full*, which tells a
   * customer the class is happening and popular. The same invariant bounds it — a section
   * marked not running that can still be bought stays on the page, in its usual state, so
   * the contradiction is seen and fixed rather than sold in secret.
   *
   * `soon` does not ask whether the variant is available. `opens_on` is the date Julie
   * announced; inventory is only how the hold is *enforced*, and it enforces nothing on an
   * untracked variant (available forever — `jbc-rollover plan` reports those and leaves them
   * alone) or one whose inventory policy is `continue`. Either would answer "yes" to
   * "available?" and be handed a Reserve button for a session that has not opened.
   *
   * `started` outranks soon, open and full: once the first class has met, inventory and
   * `opens_on` stop mattering. `retired` still outranks it — a session that has ended is not
   * "underway". See hasStarted() above for the rule and why a missing time closes early.
   *
   * ISO dates sort lexically, and 'HH:MM' does too, so the comparisons are plain string
   * compares: no date maths and no way for the two implementations to drift apart on
   * arithmetic.
   */
  function sectionState(entry, today, clock) {
    if (!entry) return 'dormant';
    if (!entry.term && !entry.available) return 'dormant';
    if (entry.not_running && !entry.available) return 'dormant';
    if (!entry.term) return 'plain';
    if (entry.ends_on && entry.ends_on < today) return 'retired';
    if (hasStarted(entry, today, clock)) return 'started';
    if (entry.opens_on && entry.opens_on > today) return 'soon';
    if (entry.available) return 'open';
    return 'full';
  }

  /* The current year, from the page's own clock: the stamped 'YYYY-MM-DD', a Date, or — only
     as a last resort — the browser's. Liquid asks `'now' | date: '%Y'` for the same thing. */
  function yearOf(today) {
    if (/^\d{4}-/.test(String(today))) return Number(String(today).slice(0, 4));
    if (today instanceof Date) return today.getFullYear();
    return new Date().getFullYear();
  }

  /* 'YYYY-MM-DD' -> "October 21", or "October 21, 2027" when the year is not the current
     one. Parsed by hand so the browser does not read a bare ISO date as UTC and shift it a
     day in US time zones. Matches snippets/jbc-date.liquid. */
  function formatDay(value, today) {
    if (typeof value !== 'string') return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m) return '';
    var year = Number(m[1]);
    var month = Number(m[2]) - 1;
    var day = Number(m[3]);
    if (month < 0 || month > 11) return '';
    var text = MONTHS[month] + ' ' + day;
    return year === yearOf(today) ? text : text + ', ' + year;
  }

  /* "Fall II / Thursday 9:30 AM" -> "Thursday 9:30 AM" */
  function slotFromVariantTitle(title) {
    if (typeof title !== 'string') return '';
    var i = title.indexOf('/');
    return (i === -1 ? title : title.slice(i + 1)).trim();
  }

  /* "Fall II / Thursday 9:30 AM" -> "Fall II" */
  function termLabelFromVariantTitle(title) {
    if (typeof title !== 'string') return '';
    var i = title.indexOf('/');
    return (i === -1 ? '' : title.slice(0, i)).trim();
  }

  function fillCopy(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /*
   * The overflow gate. Returns the target to link to, or null.
   *
   * Two rules, both learned in Phase 1, both of which make the page contradict itself when
   * they are missing:
   *
   *   - never while the section is held. A section whose registration has not opened is not
   *     full. "Opens October 21" directly above "This section is full — … has seats" is a
   *     contradiction, so the line is rendered only in the `full` state.
   *   - never across sessions. The resolved target carries its own jbc.term and it must equal
   *     the section's. A missing term on either side is an unknown, not a match, and we say
   *     nothing — otherwise a customer looking at Fall II is sent to a Fall I section that
   *     will never run again.
   */
  function overflowTarget(entry, state) {
    if (state !== 'full') return null;
    if (!entry || !entry.overflow) return null;
    var target = entry.overflow;
    if (!target.available || !target.url) return null;
    if (!entry.term || !target.term || target.term !== entry.term) return null;
    if (!slotFromVariantTitle(target.title)) return null;
    return target;
  }

  /*
   * How instructor names are written, mirroring `snippets/jbc-instructor-names.liquid`
   * (workstream O, round four, item 2).
   *
   * `jbc.instructor` is free text and the store writes two names more than one way — "Narga /
   * Julie" today, and "&", "," or " and " are all things a person would type next. Split on all
   * four, strip, de-duplicate case-insensitively, then write it the way a person would say it:
   * "Narga & Julie" for two, "Julie, Narga & Jess" for three or more.
   *
   * Two callers here, and only these two: the live region's selection sentence and the
   * overflow sentence. Everything else a customer reads is server-rendered by the snippet.
   */
  function instructorNames(raw) {
    if (typeof raw !== 'string') return '';
    /* Round six, S3: collapse runs of whitespace first, then split on the *literal* " and ",
       which is what the Liquid does. `\s+and\s+` matched things the Liquid never would — a
       newline before "and", say — so the two could disagree on the same metafield value. */
    var flat = raw.replace(/\s+/g, ' ').replace(/^ | $/g, '');
    var parts = flat.split(' and ').join('/').split('&').join('/').split(',').join('/').split('/');
    var seen = {};
    var names = [];
    for (var i = 0; i < parts.length; i++) {
      var one = parts[i].trim();
      if (!one) continue;
      var key = one.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      names.push(one);
    }
    if (names.length <= 1) return names[0] || '';
    return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
  }

  /* The sentence under a full row, from the two frozen copy settings. */
  function overflowText(target, copy, copyNoInstructor) {
    var who = instructorNames(target.instructor || '');
    var template = who ? copy : copyNoInstructor;
    if (!template) return '';
    return fillCopy(template, {
      slot: slotFromVariantTitle(target.title),
      instructor: who
    });
  }

  /*
   * What the live region says. Choosing a held or full section removes the purchase block
   * and reveals the notify box; that is a large change to make in silence, so the sentence
   * names the selection AND its state, and tells the customer what to do next where there is
   * something to do. Short on purpose — it is read out on every radio change.
   */
  function selectionSentence(entry, state, today) {
    var label = selectedLabel(entry);
    if (!label || state === 'plain' || state === 'retired' || state === 'dormant') return label;

    if (state === 'open') {
      if (typeof entry.seats === 'number' && entry.seats > 0) {
        return label + ' — open, ' + entry.seats + (entry.seats === 1 ? ' seat' : ' seats');
      }
      return label + ' — open';
    }

    if (state === 'started') {
      /* Nothing to do and nothing to offer: this one has already met. The sentence says so
         and stops, rather than inviting an email nobody will answer. */
      var began = formatDay(entry.starts_on, today);
      return label + ' — underway' + (began ? ', started ' + began : '');
    }

    if (state === 'soon') {
      var date = formatDay(entry.opens_on, today);
      return label + ' — ' + (date ? 'opens ' + date + ', ' : '')
        + 'enter your email below to be notified';
    }

    return label + ' — full, enter your email below';
  }

  /* ------------------------------------------------------- the .ics file */

  /*
   * "Add these dates to my calendar" is built here, in the browser, and handed to the anchor
   * as a `data:text/calendar` URL with a `download` attribute. No API call, no app, nothing
   * leaves the page — which is also why the sessions have to be in the JSON map already
   * (`session_dates` and `time_range`, both emitted by snippets/jbc-class-data.liquid).
   *
   * FLOATING LOCAL TIMES, on purpose. Every event is written as `DTSTART:20260917T093000`
   * with no `Z` and no `TZID`, which RFC 5545 §3.3.5 calls a floating time: it means 9:30 in
   * whatever zone the calendar is in. Students come to a studio outside Chapel Hill, so their
   * calendar is the studio's calendar, and a floating time is right for all of them without
   * shipping a VTIMEZONE block this theme has no way to keep correct across a DST rule change.
   * The one case it is wrong for is a student who keeps their calendar in another zone, which
   * is not a case this business has.
   *
   * Everything below is pure and is tested in scripts/jbc-classes.test.mjs.
   */

  function icsEscape(text) {
    return String(text)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n|[\r\n]/g, '\\n');
  }

  /* UTF-8 length of one code point, because RFC 5545 folds at 75 *octets*, not characters. */
  function utf8Bytes(ch) {
    var cp = ch.codePointAt ? ch.codePointAt(0) : ch.charCodeAt(0);
    if (cp < 0x80) return 1;
    if (cp < 0x800) return 2;
    if (cp < 0x10000) return 3;
    return 4;
  }

  function codePoints(text) {
    if (typeof Array.from === 'function') return Array.from(text);
    return String(text).split('');
  }

  /*
   * RFC 5545 §3.1: no line may exceed 75 octets, and a long one is folded by breaking it with
   * CRLF and resuming with a single space. Unfolding removes exactly that CRLF+space, so a
   * break may fall anywhere — including inside an escape — without changing the value.
   */
  function icsFold(line) {
    var chars = codePoints(line);
    var out = [];
    var current = '';
    var bytes = 0;
    for (var i = 0; i < chars.length; i++) {
      var n = utf8Bytes(chars[i]);
      if (bytes + n > 75) {
        out.push(current);
        current = '';
        bytes = 1; /* the space the join below puts back */
      }
      current += chars[i];
      bytes += n;
    }
    out.push(current);
    return out.join('\r\n ');
  }

  /* '18:30-20:30' -> { start: '183000', end: '203000' }, or null. A range we cannot read is
     null and the link is not rendered: no link is better than a wrong time. */
  function parseTimeRange(value) {
    var m = /^\s*(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*$/.exec(String(value || ''));
    if (!m) return null;
    var sh = Number(m[1]);
    var sm = Number(m[2]);
    var eh = Number(m[3]);
    var em = Number(m[4]);
    if (sh > 23 || eh > 23 || sm > 59 || em > 59) return null;
    var start = sh * 60 + sm;
    var end = eh * 60 + em;
    if (end <= start) return null;
    return { start: pad(sh) + pad(sm) + '00', end: pad(eh) + pad(em) + '00' };
  }

  /* 'YYYY-MM-DD' -> 'YYYYMMDD', or '' if it is not a date. */
  function compactDate(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    return m ? m[1] + m[2] + m[3] : '';
  }

  /* The host of an absolute URL, for the right-hand side of a UID. */
  function hostOf(url) {
    var m = /^https?:\/\/([^/?#]+)/i.exec(String(url || ''));
    return m ? m[1] : 'jbc';
  }

  /* "mixed-levels-throwing-on-the-wheel-daytime-adult-fall-i.ics" — the product's handle and
     the session as the page names it. The handle rather than `jbc.class_key` because a
     downloaded file ends up in a folder beside other downloads, where "beginner-plus" does
     not say which class it is; and the session *label* ("Fall I") rather than `jbc.term`
     ("2026-fall-i") because that is the word on the page the customer clicked. */
  function slugify(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /*
   * Workstream P, fix round four: the class time is in the name too.
   *
   * It was `<handle>-<session>.ics`, so a customer who downloaded both of Beginner Plus's
   * Fall II times got two files called
   * `mixed-levels-throwing-on-the-wheel-daytime-adult-fall-ii.ics`, and the browser named the
   * second one "(1)". Two files with one name is two files nobody can tell apart in a
   * downloads folder, which is the whole job of a filename.
   *
   * `<handle>-<session>-<day>-<start>.ics` —
   * `…-fall-ii-thursday-0930.ics`. The day comes from the parsed variant title, the start from
   * `jbc.time_range` in 24-hour form, so two times on the same day are still two names. Where
   * the range is unreadable the day alone is used, which is still better than nothing, and
   * where even the title has no slot the name is what it always was.
   */
  function icsFilename(entry, handle) {
    var parts = [];
    var stem = slugify(handle);
    if (stem) parts.push(stem);
    var label = slugify(entry && (entry.term_label || termLabelFromVariantTitle(entry.title)));
    if (label) parts.push(label);

    var slot = (entry && (entry.day_time || slotFromVariantTitle(entry.title))) || '';
    var day = slugify(String(slot).split(' ')[0]);
    if (day) parts.push(day);

    var range = parseTimeRange(entry && entry.time_range);
    if (range) parts.push(range.start.slice(0, 4));

    if (!parts.length) parts.push('class');
    return parts.join('-') + '.ics';
  }

  /*
   * One VEVENT per session date. Returns the file as a string, or null when there is nothing
   * to build from — no session dates, or no readable time range. The caller renders no link
   * at all in that case (plan T12: hidden when there are no session dates).
   *
   * `entry` is a section from the JSON map. `product` carries the page-level facts:
   *   { title, url, location, today }
   */
  function buildICS(entry, product) {
    if (!entry) return null;
    var dates = entry.session_dates;
    if (!dates || !dates.length) return null;
    var range = parseTimeRange(entry.time_range);
    if (!range) return null;

    var page = product || {};
    var variantId = (entry.variant && entry.variant.id) || entry.id || '';
    var host = hostOf(page.url);
    var stamp = (compactDate(page.today) || '19700101') + 'T000000Z';

    var slot = entry.day_time || slotFromVariantTitle(entry.title);
    var summary = 'Class: ' + (page.title || '') + (slot ? ' — ' + slot : '');
    if (entry.instructor) summary += ' (' + entry.instructor + ')';

    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//JBC//jbc-classes//EN',
      'CALSCALE:GREGORIAN'
    ];
    /* No METHOD. `METHOD:PUBLISH` is legal and harmless in most clients, but several Outlook
       builds treat any VCALENDAR carrying a METHOD as an iTIP message and open it as a
       meeting invitation from the studio, with Accept/Decline buttons and a reply nobody is
       listening for. A plain calendar object imports as plain events everywhere. */

    var events = 0;
    for (var i = 0; i < dates.length; i++) {
      var day = compactDate(dates[i]);
      if (!day) continue;
      events++;
      lines.push('BEGIN:VEVENT');
      lines.push('UID:jbc-' + variantId + '-' + day + '@' + host);
      lines.push('DTSTAMP:' + stamp);
      lines.push('DTSTART:' + day + 'T' + range.start);
      lines.push('DTEND:' + day + 'T' + range.end);
      lines.push('SUMMARY:' + icsEscape(summary));
      if (page.location) lines.push('LOCATION:' + icsEscape(page.location));
      if (page.url) lines.push('DESCRIPTION:' + icsEscape(page.url));
      lines.push('END:VEVENT');
    }
    if (!events) return null;

    lines.push('END:VCALENDAR');

    var folded = [];
    for (var j = 0; j < lines.length; j++) folded.push(icsFold(lines[j]));
    return folded.join('\r\n') + '\r\n';
  }

  /* "Fall I · Thursday 9:30 AM with Julie" */
  function selectedLabel(entry) {
    if (!entry) return '';
    var label = entry.term_label || termLabelFromVariantTitle(entry.title);
    var slot = entry.day_time || slotFromVariantTitle(entry.title);
    var text = label ? label + ' · ' + slot : slot;
    var who = instructorNames(entry.instructor || '');
    if (who) text += ' with ' + who;
    return text;
  }

  /* ----------------------------------------------------------------- DOM */

  function parseJSON(text, fallback) {
    try {
      var parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function readMap(rootEl) {
    if (rootEl.__jbcMap) return rootEl.__jbcMap;
    var el = rootEl.querySelector('[data-jbc-sections]');
    var map = el ? parseJSON(el.textContent || '', {}) : {};
    rootEl.__jbcMap = map;
    return map;
  }

  /* Everything one class product needs, scoped to its own product section — quick view puts a
     second product in the document and neither may reach into the other. */
  function contextFor(rootEl) {
    if (!rootEl.closest) return null;
    var product = rootEl.closest('.js-product_section') || rootEl.closest('.product-form-container');
    if (!product) return null;
    var container = rootEl.closest('.product-form-container') || product;
    var clock = storeClock(
      rootEl.getAttribute('data-jbc-today'),
      rootEl.getAttribute('data-jbc-offset')
    );
    return {
      root: rootEl,
      product: product,
      container: container,
      map: readMap(rootEl),
      today: clock.today,
      clock: clock.now,
      form: container.querySelector('[data-product-form]'),
      picker: container.querySelector('[data-jbc-picker]'),
      notify: container.querySelector('[data-jbc-notify]')
    };
  }

  function setting(ctx, name) {
    return ctx.root.getAttribute('data-jbc-' + name) || '';
  }

  function variantFromQuery() {
    var m = /[?&]variant=(\d+)/.exec(window.location.search || '');
    return m ? m[1] : '';
  }

  /* An own key of the section map, and nothing an object has by inheritance. */
  function hasEntry(map, key) {
    return !!map && Object.prototype.hasOwnProperty.call(map, String(key));
  }

  /* The variant Flex's form will submit: its hidden `input[name="id"]`, or '' if it has none. */
  function formVariantId(ctx) {
    var input = ctx.container.querySelector('input[name="id"], select[name="id"]');
    return input && input.value ? String(input.value).trim() : '';
  }

  /* The variant already selected: our own checked radio (server-rendered by
     `snippets/jbc-section-picker.liquid` on the class time the price states), then the form's
     hidden input, then `?variant=`, then the first section in the product's own order. */
  function selectedVariantId(ctx) {
    /* Workstream P, SEC-L8: `ctx.map[key]` finds `constructor` and `__proto__` on any object,
       so an id of "constructor" resolved to a function and was treated as a section. The map scan
       already guarded this; these three did not. */
    var checked = ctx.picker && ctx.picker.querySelector('[data-jbc-radio]:checked');
    if (checked && hasEntry(ctx.map, checked.value)) return checked.value;

    var value = formVariantId(ctx);
    if (value && hasEntry(ctx.map, value)) return value;

    var fromQuery = variantFromQuery();
    if (fromQuery && hasEntry(ctx.map, fromQuery)) return fromQuery;

    return keysInProductOrder(ctx.map)[0] || '';
  }

  /*
   * The states a customer can choose, in the order the page prefers them — the card chip's
   * ladder (`snippets/jbc-card-summary.liquid`): an `open` one, then a termless `plain` one
   * (on sale, and the chip calls it "Open"), then a held one, then a full one. One ladder for
   * the chip, the price and the page's opening selection, so a card's chip and its price name
   * the same class time and its class page opens on it (JA-2 review, 2026-10-11).
   *
   * Preference rather than document order, because the states that cannot be chosen are the
   * ones this function exists for. `retired`, `dormant` and `started` are never candidates —
   * the first two are not rendered at all, and a started row is rendered without a radio, so
   * landing the selection on one would leave the page describing a section the picker shows as
   * closed.
   */
  var SELECTABLE = ['open', 'plain', 'soon', 'full'];

  /*
   * The map scan, pure and on its own so the tests can ask it the question directly: given
   * these sections and this clock, which one would the page move the selection to? `''` means
   * none, which is a real answer — a class whose every class time has begun has nothing
   * selectable, and `snippets/jbc-section-picker.liquid` renders no radiogroup at all in that
   * state (QA round two).
   */
  function firstSelectableKey(map, today, clock) {
    var keys = keysInProductOrder(map);
    var best = '';
    var bestRung = SELECTABLE.length;
    for (var k = 0; k < keys.length; k++) {
      var state = sectionState(map[keys[k]], today, clock);
      var rung = SELECTABLE.indexOf(state);
      if (rung === -1 || rung > bestRung) continue;
      /* Within a rung the first in product order wins, except among held class times: there the
         one that opens first does, because its date is the one the chip shows. */
      if (rung < bestRung || (state === 'soon' && opensBefore(map[keys[k]], map[best]))) {
        best = keys[k];
        bestRung = rung;
      }
    }
    return best;
  }

  /* `opens_on` is ISO `YYYY-MM-DD`, which sorts as text. Strictly before, so a tie keeps the first. */
  function opensBefore(entry, other) {
    return String(entry.opens_on || '') < String(other.opens_on || '');
  }

  /*
   * The map's own keys in the order the product lists its variants — the order Liquid walks
   * them in. A JavaScript object hands back integer-like keys in ascending numeric order, not
   * the order they were written, so without `position` "the first open class time" here was
   * the one with the lowest variant id while Liquid's was the first in the product (JA-2: the
   * card's price and the class page's must name the same class time). An entry with no
   * `position` sorts after those that have one, in the object's own order: `Array#sort` is
   * stable (ES2019), so equal ranks keep the order they were pushed in.
   */
  function keysInProductOrder(map) {
    var keys = [];
    for (var key in map) {
      if (Object.prototype.hasOwnProperty.call(map, key)) keys.push(key);
    }
    var rank = function (key) {
      var position = map[key] && Number(map[key].position);
      return isFinite(position) && position > 0 ? position : Infinity;
    };
    return keys.sort(function (a, b) {
      var ra = rank(a);
      var rb = rank(b);
      return ra === rb ? 0 : (ra < rb ? -1 : 1);
    });
  }

  /*
   * JA-2 (Julie's answers, 2026-10-11): a class quotes the price of the session on sale — one
   * class time, never "from". These are the states that can be that class time: the ones a
   * customer can select. `snippets/jbc-class-price.liquid` is the same rule in Liquid.
   */
  function offersPrice(state) {
    return SELECTABLE.indexOf(state) !== -1;
  }

  /*
   * The class time the page describes, and whose price it states: the selected one if a
   * customer can act on it, otherwise the first the ladder above would choose. `''` when the
   * class has nothing a customer can act on. `snippets/jbc-class-price.liquid` makes the same
   * choice on the server, with `product.selected_variant` (a `?variant=` link) as the selection,
   * and the picker checks the radio it names.
   */
  function quotedKey(map, today, clock, selectedKey) {
    if (selectedKey && hasEntry(map, selectedKey) &&
        offersPrice(sectionState(map[String(selectedKey)], today, clock))) {
      return String(selectedKey);
    }
    return firstSelectableKey(map, today, clock);
  }

  /* The words for one class time's price, or '' when it is not one a price may be quoted for. */
  function priceText(entry, state) {
    if (!entry || !offersPrice(state) || entry.price_text == null) return '';
    return String(entry.price_text);
  }

  /* The first radio the server rendered, for when the browser's clock finds nothing a customer
     can act on that the server still did. Its state is applied as it is, so a class time the
     browser has seen begin still shows as underway. */
  function firstRadioId(ctx) {
    var radio = ctx.picker && ctx.picker.querySelector('[data-jbc-radio]');
    if (radio && hasEntry(ctx.map, radio.value)) return radio.value;
    return '';
  }

  /* --------------------------------------------------------- our own UI */

  function applyStateClass(ctx, state) {
    for (var i = 0; i < STATES.length; i++) {
      ctx.product.classList.remove('jbc-state--' + STATES[i]);
    }
    if (state) ctx.product.classList.add('jbc-state--' + state);
  }

  /*
   * Workstream P, SEC-L7. The radio was found with a selector built by concatenation, so a
   * variant id carrying a quote would have produced an invalid selector and `querySelector`
   * throws on those — the whole picker would have stopped on one bad id. Shopify's own ids are
   * digits, so this never happened; it is the shape that is wrong, not the data. Reading the
   * radios and comparing `.value` cannot be broken by a value, and needs no `CSS.escape`
   * polyfill for the older browsers this file still supports.
   */
  function syncRadio(ctx, id) {
    if (!ctx.picker) return;
    var wanted = String(id);
    var radios = ctx.picker.querySelectorAll('[data-jbc-radio]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value !== wanted) continue;
      if (!radios[i].checked) radios[i].checked = true;
      return;
    }
  }

  /*
   * The notify box's `contact[body]`. A held class is not "back in stock": it has not opened
   * yet, so the sentence Julie receives has to say so. A full one keeps Flex's stock wording
   * with the section appended, as `selectCallback` does for every other product.
   */
  function updateNotifyBody(ctx, entry, id, held) {
    if (!ctx.notify) return;
    var field = ctx.notify.querySelector('[data-jbc-notify-body-field]');
    if (!field) return;

    if (typeof field.dataset.jbcBase !== 'string') {
      field.dataset.jbcBase = field.value || '';
    }

    var title = entry.title || '';
    if (held) {
      var prefix = setting(ctx, 'notify-body');
      if (!prefix) return;
      /* Absolute, from the data snippet (`shop.url` + `product.url`), so the line Julie gets
         emailed is a link she can click. Never `canonical_url` and never `window.location`:
         in quick view both name the collection page, not the class. */
      var url = (setting(ctx, 'product-url') || window.location.href).split('?')[0].split('#')[0];
      field.value = prefix + ' ' + title + ' | ' + url + '?variant=' + id;
    } else {
      field.value = field.dataset.jbcBase + (title ? ' - ' + title : '');
    }
  }

  function updateNotify(ctx, entry, id, state) {
    if (!ctx.notify) return;
    var held = state === 'soon';
    var label = ctx.notify.querySelector('[data-jbc-notify-label]');
    var copy = held ? setting(ctx, 'notify-held-copy') : setting(ctx, 'notify-full-copy');
    if (label && copy) label.textContent = copy;

    ctx.notify.hidden = !(state === 'soon' || state === 'full');
    updateNotifyBody(ctx, entry, id, held);
  }

  /* The overflow line belongs to the row, not to the selection, so every full row that
     passes the gate carries one. Rendered once per scan. */
  function renderOverflowLines(ctx) {
    if (!ctx.picker) return;
    var lines = ctx.picker.querySelectorAll('[data-jbc-row-overflow]');
    for (var i = 0; i < lines.length; i++) {
      var el = lines[i];
      var id = el.getAttribute('data-jbc-row-overflow');
      var entry = ctx.map[id];
      var target = overflowTarget(entry, sectionState(entry, ctx.today, ctx.clock));
      el.textContent = '';
      el.hidden = true;
      if (!target) continue;

      var text = overflowText(
        target,
        setting(ctx, 'overflow-copy'),
        setting(ctx, 'overflow-copy-no-instructor')
      );
      if (!text) continue;

      var link = document.createElement('a');
      link.href = target.url;
      link.textContent = text;
      el.appendChild(link);
      el.hidden = false;
    }
  }

  /**
   * Whether a class time in this state offers "Add these dates to my calendar": only once its
   * session has opened — `open`, or `full`, where people have registered. Not `soon`: a held
   * session's dates are announced, but Julie does not want them in calendars before a seat can
   * be bought (her notes, 2026-10-09). A registered student also has them in the welcome email.
   *
   * `snippets/jbc-section-picker-row.liquid` renders the slot on the same two states; a test
   * holds the two to one set.
   *
   * @param {string} state - A rung of `sectionState()`.
   * @returns {boolean}
   */
  function offersCalendar(state) {
    return state === 'open' || state === 'full';
  }

  /*
   * "Add these dates to my calendar", one link per row whose state `offersCalendar()`.
   *
   * The link is a sibling of the row's `<label>`, not a child of it: a `<label>` may not
   * contain interactive content, and an anchor inside one would also toggle the radio it
   * wraps. Rendered once per scan, like the overflow line, and absent — never wrong — when
   * the section has no session dates or no readable time range.
   */
  function renderCalendarLinks(ctx) {
    if (!ctx.picker) return;
    var copy = setting(ctx, 'calendar-copy');
    var slots = ctx.picker.querySelectorAll('[data-jbc-row-calendar]');
    for (var i = 0; i < slots.length; i++) {
      var el = slots[i];
      el.textContent = '';
      el.hidden = true;
      if (!copy) continue;

      var entry = ctx.map[el.getAttribute('data-jbc-row-calendar')];
      if (!offersCalendar(sectionState(entry, ctx.today, ctx.clock))) continue;

      var ics = buildICS(entry, {
        title: setting(ctx, 'product-title'),
        url: setting(ctx, 'product-url'),
        location: setting(ctx, 'location'),
        today: ctx.today
      });
      if (!ics) continue;

      var link = document.createElement('a');
      link.className = 'jbc-row__calendar-link';
      link.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
      link.setAttribute('download', icsFilename(entry, setting(ctx, 'product-handle')));
      link.textContent = copy;
      /* Several rows carry the same words, so name which section this one is for. The
         visible text is the start of the accessible name (WCAG 2.5.3). */
      var label = selectedLabel(entry);
      if (label) link.setAttribute('aria-label', copy + ': ' + label);
      el.appendChild(link);
      el.hidden = false;
    }
  }

  /*
   * The class page's one price (`snippets/jbc-product-price.liquid`) follows the selection: the
   * selected class time's own price, or nothing — hidden — if it is not one a price may be
   * quoted for (JA-2). Flex's price UI is not on a class page, so this is the only writer.
   */
  function renderPrice(ctx, entry, state) {
    var el = ctx.product.querySelector('[data-jbc-price]');
    if (!el) return;
    var text = priceText(entry, state);
    el.textContent = text;
    el.hidden = !text;
  }

  function applyState(ctx, id) {
    var entry = hasEntry(ctx.map, id) ? ctx.map[String(id)] : null;
    if (!entry) {
      applyStateClass(ctx, '');
      renderPrice(ctx, null, '');
      if (ctx.notify) ctx.notify.hidden = true;
      ctx.root.__jbcEntry = null;
      return;
    }

    ctx.root.__jbcEntry = entry;
    var state = sectionState(entry, ctx.today, ctx.clock);
    applyStateClass(ctx, state);
    syncRadio(ctx, id);
    renderPrice(ctx, entry, state);

    var label = ctx.picker && ctx.picker.querySelector('[data-jbc-selected]');
    if (label) label.textContent = selectionSentence(entry, state, ctx.today);

    updateNotify(ctx, entry, id, state);
  }

  /* ------------------------------------------------ telling Flex about it */

  function readProductPayload(form) {
    return parseJSON(form && form.dataset ? form.dataset.product : 'null', null);
  }

  /* Best effort: keep Flex's own option controls showing what our radios say. The hidden
     `input[name="id"]` above is what actually submits, so a value the picker never rendered
     (Flex's "hide sold-out options" setting) costs the mirror, not the purchase. Values are
     matched by text rather than by position, because an option's values are unique within it
     and positions are not guaranteed to line up with `variant.options`. */
  function mirrorOptions(ctx, entry) {
    var picker = ctx.container.querySelector('kit-variant-picker');
    var options = entry.variant && entry.variant.options;
    if (!picker || !options || !options.length) return;

    var wanted = {};
    for (var i = 0; i < options.length; i++) wanted[String(options[i])] = true;

    var selects = picker.querySelectorAll('select[data-option-name]');
    for (var s = 0; s < selects.length; s++) {
      var select = selects[s];
      for (var o = 0; o < select.options.length; o++) {
        if (wanted[select.options[o].value]) {
          select.selectedIndex = o;
          break;
        }
      }
    }

    var radios = picker.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (wanted[radios[r].value]) radios[r].checked = true;
    }
  }

  function setHiddenVariantId(ctx, id) {
    var inputs = ctx.container.querySelectorAll('input[name="id"], select[name="id"]');
    for (var i = 0; i < inputs.length; i++) inputs[i].value = id;
  }

  /* Only where the product IS the page. Flex passes `update_url: false` from quick view and
     from the featured-product section, and kit honours it by not rendering `data-update-url`;
     without the same check, picking a section in a quick view rewrote the URL of the
     collection page behind it. */
  function updateUrl(ctx, id) {
    if (setting(ctx, 'update-url') !== 'true') return;
    if (!window.history || !window.history.replaceState) return;
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* a URL we cannot rewrite is not worth a broken click */
    }
  }

  function dispatchVariantChange(ctx, entry) {
    if (!ctx.form || typeof window.CustomEvent !== 'function') return;
    ctx.form.dispatchEvent(new window.CustomEvent('variant-change', {
      bubbles: true,
      detail: {
        product: readProductPayload(ctx.form),
        variant: entry.variant || null,
        state: null
      }
    }));
  }

  function selectVariant(ctx, id) {
    var entry = ctx.map[String(id)];
    if (!entry) return;
    setHiddenVariantId(ctx, id);
    mirrorOptions(ctx, entry);
    dispatchVariantChange(ctx, entry);
    updateUrl(ctx, id);
    applyState(ctx, id);
  }

  /* ------------------------------------------------------------ bindings */

  /* Fire-and-forget tag of the notify: list. Never blocks or alters the /contact POST, which
     stays the system of record. A CORS *simple* request on purpose: `application/json` forces
     a preflight, and /contact navigates away the instant it submits, which can cancel the
     in-flight OPTIONS and lose the POST with it. The API parses text/plain for this route
     (services/api/src/routes/notify.ts). */
  function bindNotify(ctx) {
    if (!ctx.notify) return;
    var form = ctx.notify.querySelector('form');
    if (!form || form.__jbcBound) return;
    form.__jbcBound = true;

    form.addEventListener('submit', function () {
      try {
        var endpoint = setting(ctx, 'notify-endpoint').trim();
        var entry = ctx.root.__jbcEntry;
        if (!endpoint || !entry || !entry.term) return;
        if (typeof window.fetch !== 'function') return;

        var emailInput = form.querySelector('input[name="contact[email]"]');
        var honeypot = form.querySelector('input[name="contact_ref"]');
        var email = emailInput ? emailInput.value : '';
        if (!email) return;

        window.fetch(endpoint.replace(/\/+$/, '') + '/notify', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({
            email: email,
            class_key: setting(ctx, 'class-key'),
            term: entry.term,
            contact_ref: honeypot ? honeypot.value : ''
          }),
          keepalive: true,
          mode: 'cors'
        }).catch(function () { /* the contact email is the system of record */ });
      } catch (e) {
        /* never interfere with the real submission */
      }
    }, false);
  }

  function bindPicker(ctx) {
    if (!ctx.picker || ctx.picker.__jbcBound) return;
    ctx.picker.__jbcBound = true;

    ctx.picker.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.hasAttribute || !target.hasAttribute('data-jbc-radio')) return;
      try {
        selectVariant(ctx, target.value);
      } catch (e) {
        /* stock Flex behaviour stands */
      }
    }, false);
  }

  /* Flex's own listener sits on the form; ours sits on the container, so bubbling guarantees
     Flex has finished before we read anything. Covers the theme editor, the browser's back
     button and any future Flex path that changes the variant without going through us. */
  function bindVariantChange(ctx) {
    if (ctx.product.__jbcBound) return;
    ctx.product.__jbcBound = true;

    ctx.product.addEventListener('variant-change', function (event) {
      try {
        var variant = event.detail && event.detail.variant;
        if (!variant || !variant.id) return;
        applyState(ctx, variant.id);
      } catch (e) {
        /* stock Flex behaviour stands */
      }
    }, false);
  }

  /*
   * Flex's option controls stay in the DOM — the hidden `input[name="id"]` inside the picker
   * is what Shopify's form actually submits, and removing it would break add-to-cart — but
   * once our radiogroup is live they are a duplicate of it, and a worse one: their option
   * labels read "Fall II - Sold Out", "Winter - Sold Out", which is both wrong about a held
   * session and about a placeholder nobody can buy.
   *
   * So they are taken out of the accessibility tree as well as out of the tab order. That is
   * safe only because nothing inside is focusable any more — `aria-hidden` over a focusable
   * control is its own bug — which is why the tabindex demotion happens first. The hidden
   * input still submits: `aria-hidden` hides from assistive tech, not from the form.
   */
  function demoteNativePicker(ctx) {
    var picker = ctx.container.querySelector('kit-variant-picker');
    if (!picker) return;
    var controls = picker.querySelectorAll('select, input[type="radio"], a, button');
    for (var i = 0; i < controls.length; i++) controls[i].setAttribute('tabindex', '-1');
    picker.setAttribute('aria-hidden', 'true');
  }

  /*
   * Each radio's accessible name, spelled out rather than inferred.
   *
   * The row's `<label>` wraps the input, but the browser was reading the radio's *value* —
   * a variant id — as its name. An explicit `aria-label` settles it, and it uses the same
   * sentence builder as the live region so a customer hears the same words when they arrive
   * on a radio and when the page reacts to it.
   */
  function labelRadios(ctx) {
    if (!ctx.picker) return;
    var radios = ctx.picker.querySelectorAll('[data-jbc-radio]');
    for (var i = 0; i < radios.length; i++) {
      var entry = ctx.map[radios[i].value];
      if (!entry) continue;
      var sentence = selectionSentence(entry, sectionState(entry, ctx.today, ctx.clock), ctx.today);
      if (sentence) radios[i].setAttribute('aria-label', sentence);
    }
  }

  /* The schedule links a held or full section as `…?variant=<id>#jbc-notify`. The browser
     cannot jump to the box at load because it is still `hidden` then, so once the state has
     been applied and it is visible, bring it into view once. */
  function revealNotifyAnchor(ctx) {
    if (window.location.hash !== '#jbc-notify') return;
    if (!ctx.notify || ctx.notify.hidden || ctx.notify.__jbcRevealed) return;
    ctx.notify.__jbcRevealed = true;
    if (typeof ctx.notify.scrollIntoView !== 'function') return;
    ctx.notify.scrollIntoView({ block: 'center' });
  }

  function setUp(ctx) {
    ctx.product.classList.add('jbc-product');
    bindVariantChange(ctx);
    bindPicker(ctx);
    bindNotify(ctx);
    renderOverflowLines(ctx);
    renderCalendarLinks(ctx);

    if (ctx.picker) {
      ctx.product.classList.add('jbc-has-js');
      demoteNativePicker(ctx);
      labelRadios(ctx);
    }

    /* An old bookmark or order link can name a retired section, and Shopify's own
       `selected_or_first_available_variant` can land on a dormant placeholder when nothing is
       available or on a section whose first class has already met. None of the three is
       selectable in the picker, so move the selection to a section the customer can actually
       choose and tell Flex and the URL about it, rather than leaving the page describing
       something it does not offer.

       Flex's form is moved too when it disagrees with the selection (JA-2 review). The picker
       checks the class time the price states, but Flex fills its hidden `input[name="id"]` —
       what Add to cart submits — from `selected_or_first_available_variant`, the first variant
       in stock: a class time that has begun and not yet been zeroed is exactly that. */
    var selected = selectedVariantId(ctx);
    var id = quotedKey(ctx.map, ctx.today, ctx.clock, selected) || firstRadioId(ctx);
    var formId = formVariantId(ctx);
    if (id && (id !== String(selected) || (formId && formId !== id))) {
      selectVariant(ctx, id);
      revealNotifyAnchor(ctx);
      return;
    }

    applyState(ctx, selected);
    revealNotifyAnchor(ctx);
  }

  function scan() {
    var roots = document.querySelectorAll('[data-jbc-class]');
    for (var i = 0; i < roots.length; i++) {
      try {
        var ctx = contextFor(roots[i]);
        if (ctx) setUp(ctx);
      } catch (e) {
        /* one broken product must not take the others down */
      }
    }
  }

  /* Quick view injects a fresh product long after DOMContentLoaded and this file does not
     re-run; the theme editor reloads sections the same way. We observe the container Flex
     fills rather than hooking its open handler, so no Flex asset is touched. Binding is
     idempotent — every element we bind carries a marker. */
  function watchForRerenders() {
    document.addEventListener('shopify:section:load', function () {
      try { scan(); } catch (e) { /* stock Flex behaviour stands */ }
    }, false);

    if (typeof window.MutationObserver !== 'function') return;
    var container = document.querySelector('.js-quick-shop');
    if (!container) return;
    new window.MutationObserver(function () {
      try { scan(); } catch (e) { /* stock Flex behaviour stands */ }
    }).observe(container, { childList: true, subtree: true });
  }

  function init() {
    try { scan(); } catch (e) { /* stock Flex behaviour stands */ }
    try { watchForRerenders(); } catch (e) { /* stock Flex behaviour stands */ }
  }

  function start() {
    if (window.__jbcClassesLoaded) return;
    window.__jbcClassesLoaded = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  return {
    todayISO: todayISO,
    CLOSED_CLOCK: CLOSED_CLOCK,
    storeToday: storeToday,
    parseOffset: parseOffset,
    storeClock: storeClock,
    startClockOf: startClockOf,
    hasStarted: hasStarted,
    firstSelectableKey: firstSelectableKey,
    offersPrice: offersPrice,
    quotedKey: quotedKey,
    priceText: priceText,
    renderPrice: renderPrice,
    applyState: applyState,
    selectedVariantId: selectedVariantId,
    setUp: setUp,
    sectionState: sectionState,
    formatDay: formatDay,
    slotFromVariantTitle: slotFromVariantTitle,
    instructorNames: instructorNames,
    hasEntry: hasEntry,
    termLabelFromVariantTitle: termLabelFromVariantTitle,
    fillCopy: fillCopy,
    overflowTarget: overflowTarget,
    overflowText: overflowText,
    icsEscape: icsEscape,
    icsFold: icsFold,
    icsFilename: icsFilename,
    slugify: slugify,
    parseTimeRange: parseTimeRange,
    buildICS: buildICS,
    offersCalendar: offersCalendar,
    selectedLabel: selectedLabel,
    selectionSentence: selectionSentence,
    start: start
  };
}));
